import React, { ReactElement } from 'react';
import { Toolbar } from '@material-ui/core';
import * as NavigationSelectors from 'redux/reducers/gl_navigation_reducer/selectors';
import useStyles from './styles';
import clsx from 'clsx';
import { SideBarWidth } from 'config';
import useScrollTrigger from '@material-ui/core/useScrollTrigger';

interface IHeaderProps {
  children: ReactElement;
  style?: React.CSSProperties;
  className?: string;
  enableElevation?: boolean;
}

interface Props {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window?: () => Window;
  children: React.ReactElement;
}

const ElevationScroll = React.memo((props: Props) => {
  const { children } = props;
  // Note that you normally won't need to set the window ref as useScrollTrigger
  // will default to window.
  // This is only being set here because the demo is in an iframe.
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window,
  });

  return React.cloneElement(children, {
    elevation: trigger ? true : false,
  });
});

export default function HeaderBar({
  children,
  style = {},
  className = '',
  enableElevation = true,
}: IHeaderProps) {
  const classes = useStyles();

  return (
    <React.Fragment>
      {enableElevation ? (
        <ElevationScroll>
          <ToolbarWrapper style={style} className={className}>
            {children}
          </ToolbarWrapper>
        </ElevationScroll>
      ) : (
        <ToolbarWrapper style={style} className={className}>
          {children}
        </ToolbarWrapper>
      )}
      <Toolbar
        style={{ visibility: 'hidden' }}
        className={clsx({
          [classes.toolbar]: true,
          className,
        })}
      />
    </React.Fragment>
  );
}

interface IToolbarProps extends IHeaderProps {
  elevation?: boolean;
}

const ToolbarWrapper = ({
  children,
  className = '',
  style = {},
  elevation,
}: IToolbarProps) => {
  const classes = useStyles();
  const sideBarISVisible = NavigationSelectors.useSidebarIsVisible();

  return (
    <Toolbar
      className={clsx({
        [classes.sticky_bar]: true,
        [classes.stickyBarTransition]: sideBarISVisible,
        [classes.appBarOpen]: !sideBarISVisible,
        [classes.toolbar]: true,
        [classes.elevateToolbar]: Boolean(elevation),
        className,
      })}
      style={{
        ...style,
        width: sideBarISVisible ? `calc(100% - ${SideBarWidth}px)` : '100%',
      }}
    >
      {children}
    </Toolbar>
  );
};
